import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiResponse } from '../../types/common';

class ApiService {
  private client: AxiosInstance;
  private token: string | null = null;
  private eventHandlers: Map<string, Function[]> = new Map();
  private isRefreshing: boolean = false;
  private refreshPromise: Promise<void> | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(baseURL?: string) {
    // 默认使用环境变量中的API地址，如果没有则使用传入的baseURL，最后回退到相对路径
    const apiUrl = this.getApiBaseUrl() || baseURL || '/api';

    this.client = axios.create({
      baseURL: apiUrl,
      timeout: 120000, // 增加到120秒，给图片分析足够时间
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error) => {
        const originalRequest = error.config;
        
        // 如果是 401 错误且不是刷新 token 的请求，尝试刷新 token
        if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/refresh-token')) {
          originalRequest._retry = true;
          
          try {
            await this.handleTokenRefresh();
            // Token 刷新成功，重试原始请求
            return this.client(originalRequest);
          } catch (refreshError) {
            // Token 刷新失败，触发登出事件
            this.emit('auth_failed');
            return Promise.reject(refreshError);
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  setToken(token: string) {
    this.token = token;
    // 设置主动刷新定时器
    this.scheduleTokenRefresh(token);
  }

  clearToken() {
    this.token = null;
    // 清除刷新定时器
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * 调度 token 自动刷新
   * 在 token 过期前 5 分钟自动刷新
   */
  private scheduleTokenRefresh(token: string): void {
    // 清除旧的定时器
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    try {
      // 解析 JWT payload
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiresAt = payload.exp * 1000; // 转换为毫秒
      const now = Date.now();
      const timeUntilExpiry = expiresAt - now;

      // 提前 5 分钟刷新，或者在过期时间的 90% 时刷新（取较大值）
      const refreshTime = Math.max(
        timeUntilExpiry - (5 * 60 * 1000), // 提前 5 分钟
        timeUntilExpiry * 0.9 // 或过期时间的 90%
      );

      if (refreshTime > 0) {
        console.log(`[ApiService] Token 将在 ${Math.round(refreshTime / 1000 / 60)} 分钟后自动刷新`);

        this.refreshTimer = setTimeout(async () => {
          console.log('[ApiService] 主动刷新 token（定时器触发）');
          await this.handleTokenRefresh();
        }, refreshTime);
      } else {
        // Token 已经过期或即将过期，立即刷新
        console.log('[ApiService] Token 已过期，立即刷新');
        this.handleTokenRefresh();
      }
    } catch (error) {
      console.warn('[ApiService] 无法解析 token 过期时间，将使用被动刷新');
    }
  }

  /**
   * 获取API基础URL
   */
  private getApiBaseUrl(): string | undefined {
    // 在浏览器环境中，尝试从环境变量获取
    if (typeof window !== 'undefined') {
      // Vite环境变量
      try {
        if (typeof import.meta !== 'undefined') {
          const meta = import.meta as any;
          if (meta.env?.VITE_API_BASE_URL) {
            return meta.env.VITE_API_BASE_URL;
          }
        }
      } catch (e) {
        // import.meta 可能在某些环境下不可用
      }

      // 检查全局配置
      const globalConfig = (window as any).__APP_CONFIG__;
      if (globalConfig?.apiBaseUrl) {
        return globalConfig.apiBaseUrl;
      }
    }

    // Node.js环境
    if (typeof process !== 'undefined' && process.env) {
      return process.env.VITE_API_BASE_URL || process.env.API_BASE_URL;
    }

    return undefined;
  }

  private async handleTokenRefresh(): Promise<void> {
    // 如果正在刷新，等待刷新完成
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    // 开始刷新
    this.isRefreshing = true;
    this.refreshPromise = this.doRefreshToken();

    try {
      await this.refreshPromise;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async doRefreshToken(): Promise<void> {
    const refreshToken = localStorage.getItem('auth_refresh_token');

    if (!refreshToken) {
      console.log('[ApiService] 没有 refresh token，执行登出');
      this.handleLogout();
      throw new Error('No refresh token available');
    }

    try {
      const response = await this.client.post('/auth/refresh-token', { refreshToken });
      const result = response.data;

      if (result.success) {
        const { tokens } = result.data;
        const { accessToken, refreshToken: newRefreshToken } = tokens;

        // 更新 token（会自动设置新的定时器）
        this.setToken(accessToken);
        localStorage.setItem('auth_token', accessToken);
        localStorage.setItem('auth_refresh_token', newRefreshToken);

        // 触发 token 刷新成功事件，通知其他服务
        console.log('[ApiService] Token 刷新成功，通知其他服务');
        this.emit('token_refreshed', { accessToken, refreshToken: newRefreshToken });
      } else {
        console.log('[ApiService] Token 刷新失败，执行登出');
        this.handleLogout();
        throw new Error('Token refresh failed');
      }
    } catch (error) {
      console.error('[ApiService] Token 刷新异常，执行登出', error);
      this.handleLogout();
      throw error;
    }
  }

  /**
   * 处理登出
   * 清除所有认证状态并跳转到登录页
   */
  private handleLogout(): void {
    // 清除定时器
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    // 清除 token
    this.clearToken();

    // 清除本地存储
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_refresh_token');
    localStorage.removeItem('auth_user');

    // 触发登出事件，通知其他服务
    console.log('[ApiService] 触发登出事件');
    this.emit('logout');

    // 跳转到登录页
    window.location.href = '/login';
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.get(url, config);
      return response.data;
    } catch (error: any) {
      return this.handleError(error);
    }
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.post(url, data, config);
      return response.data;
    } catch (error: any) {
      return this.handleError(error);
    }
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.put(url, data, config);
      return response.data;
    } catch (error: any) {
      return this.handleError(error);
    }
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.delete(url, config);
      return response.data;
    } catch (error: any) {
      return this.handleError(error);
    }
  }

  async uploadFile<T = any>(url: string, file: File, onProgress?: (progress: number) => void): Promise<ApiResponse<T>> {

    const formData = new FormData();
    formData.append('image', file);

    try {

      const response = await this.client.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 180000, // 图片上传和分析设置3分钟超时
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      });

      return response.data;
    } catch (error: any) {
      return this.handleError(error);
    }
  }

  private handleError(error: any): ApiResponse {
    if (error.response) {

      return error.response.data;
    } else if (error.request) {

      return {
        success: false,
        error: {
          code: 'NO_RESPONSE',
          message: '服务器无响应，请检查服务器是否启动',
          details: {
            readyState: error.request.readyState,
            status: error.request.status,
            responseURL: error.request.responseURL
          },
        },
      };
    } else {

      return {
        success: false,
        error: {
          code: 'REQUEST_ERROR',
          message: error.message || '请求配置错误',
          details: error,
        },
      };
    }
  }

  /**
   * 注册事件监听器
   */
  on(event: string, callback: Function): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event)!.push(callback);
  }

  /**
   * 移除事件监听器
   */
  off(event: string, callback?: Function): void {
    if (!this.eventHandlers.has(event)) return;

    if (callback) {
      const handlers = this.eventHandlers.get(event)!;
      const index = handlers.indexOf(callback);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    } else {
      this.eventHandlers.delete(event);
    }
  }

  /**
   * 触发事件
   */
  private emit(event: string, ...args: any[]): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(...args);
        } catch (error) {
          console.error('[ApiService] 事件处理器错误:', error);
        }
      });
    }
  }
}

export const apiService = new ApiService();
export default ApiService;